/** Helpers & constants for voucher-reminder CMS (matches um-backend) */

export const VR_STATUS = {
  PENDING: 'pending_approval',
  APPROVED: 'approved',
  ACTIVE: 'active',
  PAUSED: 'paused',
  REJECTED: 'rejected',
  ENDED: 'ended',
} as const;

export type VrStatus = (typeof VR_STATUS)[keyof typeof VR_STATUS];

export const VR_STATUS_OPTIONS = [
  { value: VR_STATUS.PENDING, labelId: 'voucherReminder.status.pending_approval', color: 'gold' },
  { value: VR_STATUS.APPROVED, labelId: 'voucherReminder.status.approved', color: 'blue' },
  { value: VR_STATUS.ACTIVE, labelId: 'voucherReminder.status.active', color: 'green' },
  { value: VR_STATUS.PAUSED, labelId: 'voucherReminder.status.paused', color: 'orange' },
  { value: VR_STATUS.REJECTED, labelId: 'voucherReminder.status.rejected', color: 'red' },
  { value: VR_STATUS.ENDED, labelId: 'voucherReminder.status.ended', color: 'default' },
];

export const VR_TRIGGER_OPTIONS = [
  { value: 'expiring_in_days', labelId: 'voucherReminder.trigger.expiring_in_days' },
  { value: 'new_issued', labelId: 'voucherReminder.trigger.new_issued' },
  { value: 'unused_after_days', labelId: 'voucherReminder.trigger.unused_after_days' },
];

export const VR_CHANNEL_OPTIONS = [
  { value: 'sms', labelId: 'voucherReminder.channel.sms' },
  { value: 'in_app', labelId: 'voucherReminder.channel.in_app' },
];

export const VR_DEFAULT_CHANNELS = ['in_app'] as const;

export const VR_SCHEDULE_OPTIONS = [
  { value: 'daily_at', labelId: 'voucherReminder.schedule.daily_at' },
  { value: 'now', labelId: 'voucherReminder.schedule.now' },
];

type IntlLike = { formatMessage: (desc: { id: string }, values?: Record<string, any>) => string };

export const statusMeta = (status: string | undefined, intl: IntlLike) => {
  const found = VR_STATUS_OPTIONS.find((s) => s.value === status);
  if (!found) {
    return {
      value: status || '',
      label: status || intl.formatMessage({ id: 'voucherReminder.empty' }),
      color: 'default',
    };
  }
  return {
    value: found.value,
    label: intl.formatMessage({ id: found.labelId }),
    color: found.color,
  };
};

export const triggerLabel = (type: string | undefined, intl: IntlLike) => {
  const found = VR_TRIGGER_OPTIONS.find((t) => t.value === type);
  if (!found) return type || intl.formatMessage({ id: 'voucherReminder.empty' });
  return intl.formatMessage({ id: found.labelId });
};

/** ms from 00:00 → "HH:mm" (backend stores BigInt ms) */
export function msToScheduleTime(
  ms: number | string | bigint | null | undefined
): string | null {
  if (ms == null || ms === '') return null;
  const total = Number(ms);
  if (!Number.isFinite(total) || total < 0) return null;
  const hours = Math.floor(total / 3_600_000);
  const minutes = Math.floor((total % 3_600_000) / 60_000);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function toMs(value: number | string | bigint | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function formatTs(
  value: number | string | bigint | null | undefined,
  _locale?: string
) {
  const n = toMs(value);
  if (n == null) return '—';
  const d = new Date(n);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

export function parseIdList(raw: unknown): number[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
}

export function getRejectReason(campaign: any): string {
  if (!campaign) return '';
  return String(
    campaign.rejectReason ?? campaign.rejectedReason ?? campaign.reason ?? ''
  ).trim();
}

/** mode trên pageInfo.buttons — dùng để phân quyền theo role */
export const VR_BUTTON_MODE = {
  CREATE: 'create',
  EDIT: 'edit',
  APPROVE: 'approve',
  REJECT: 'reject',
  PAUSE: 'pause',
  RESUME: 'resume',
  END: 'end',
} as const;

const VR_ACTION_MODES = Object.values(VR_BUTTON_MODE);

export function findPageButton(pageInfo: any, mode: string) {
  const buttons = Array.isArray(pageInfo?.buttons) ? pageInfo.buttons : [];
  return buttons.find((b: any) => String(b?.mode || '') === mode) || null;
}

/** Page đã cấu hình ít nhất 1 nút action voucher-reminder */
export function pageHasVrActionButtons(pageInfo: any) {
  return VR_ACTION_MODES.some((mode) => !!findPageButton(pageInfo, mode));
}

/**
 * Quyền hiện nút theo pageInfo.buttons.roles
 * - Chưa cấu hình action nào trên page → cho phép (giữ UX cũ, chỉ lọc theo status)
 * - Đã cấu hình → chỉ hiện mode có trong buttons + role khớp (roles rỗng = mọi role)
 */
export function canUsePageButton(
  pageInfo: any,
  mode: string,
  roleId?: number | null
) {
  if (!pageHasVrActionButtons(pageInfo)) return true;
  const btn = findPageButton(pageInfo, mode);
  if (!btn) return false;
  const roles = btn.roles;
  if (!Array.isArray(roles) || roles.length === 0) return true;
  if (roleId == null) return false;
  return roles.map((r: any) => Number(r)).includes(Number(roleId));
}

export function getPageButtonTitle(
  btn: any,
  locale: string | undefined,
  fallback: string
) {
  if (!btn) return fallback;
  const lang = String(locale || 'vi').split('-')[0];
  if (lang === 'la') return btn.titleLa || btn.title || fallback;
  if (lang === 'en') return btn.titleEn || btn.title || fallback;
  return btn.title || fallback;
}

export const VR_CONTENT_LOCALES = [
  { language: 'vi', labelId: 'voucherReminder.lang.vi' },
  { language: 'en', labelId: 'voucherReminder.lang.en' },
  { language: 'la', labelId: 'voucherReminder.lang.la' },
  { language: 'zh', labelId: 'voucherReminder.lang.zh' },
] as const;

export type ReminderContentLang = (typeof VR_CONTENT_LOCALES)[number]['language'];

export type ReminderTranslation = {
  language: ReminderContentLang;
  title: string;
  body: string;
};

/** Khớp um-backend REMINDER_TEMPLATE_VARIABLES — fallback khi API không khả dụng */
export type VrTemplateVarLocale = 'vi' | 'en' | 'la';

export type VrReminderTemplateVariable = {
  key: string;
  token: string;
  description: string;
};

export function normalizeVrCmsLocale(locale?: string): VrTemplateVarLocale {
  const lang = String(locale || 'vi').split(/[-_]/)[0].toLowerCase();
  if (lang === 'en') return 'en';
  if (lang === 'la' || lang === 'lo') return 'la';
  return 'vi';
}

const VR_TEMPLATE_VAR_FALLBACK_TEXT: Record<
  string,
  Record<VrTemplateVarLocale, string>
> = {
  code: {
    vi: 'Mã voucher (code.code)',
    en: 'Voucher code (code.code)',
    la: 'ລະຫັດບັດສ່ວນຫຼຸດ (code.code)',
  },
  voucher_name: {
    vi: 'Tên voucher',
    en: 'Voucher name',
    la: 'ຊື່ບັດສ່ວນຫຼຸດ',
  },
  partner_name: {
    vi: 'Tên đối tác',
    en: 'Partner name',
    la: 'ຊື່ຄູ່ຮ່ວມງານ',
  },
  expired_date: {
    vi: 'Ngày hết hạn (DD/MM/YYYY)',
    en: 'Expiry date (DD/MM/YYYY)',
    la: 'ວັນຫມົດອາຍຸ (DD/MM/YYYY)',
  },
  phone: {
    vi: 'Số điện thoại',
    en: 'Phone number',
    la: 'ເບີໂທລະສັບ',
  },
  voucher_value: {
    vi: 'Giá trị voucher',
    en: 'Voucher value',
    la: 'ມູນຄ່າບັດສ່ວນຫຼຸດ',
  },
  deeplink: {
    vi: 'Deep link campaign (có thể chứa token khác)',
    en: 'Campaign deep link (may contain other tokens)',
    la: 'ລິ້ງເຊື່ອມຕໍ່ແຄມເປນ (ອາດມີ token ອື່ນ)',
  },
};

const VR_TEMPLATE_VAR_FALLBACK_TOKENS: Array<{ key: string; token: string }> = [
  { key: 'code', token: '$code$' },
  { key: 'voucher_name', token: '$voucher_name$' },
  { key: 'partner_name', token: '$partner_name$' },
  { key: 'expired_date', token: '$expired_date$' },
  { key: 'phone', token: '$phone$' },
  { key: 'voucher_value', token: '$voucher_value$' },
  { key: 'deeplink', token: '$deeplink$' },
];

export function getVoucherReminderTemplateVariablesFallback(
  locale: VrTemplateVarLocale = 'vi'
): VrReminderTemplateVariable[] {
  return VR_TEMPLATE_VAR_FALLBACK_TOKENS.map(({ key, token }) => ({
    key,
    token,
    description:
      VR_TEMPLATE_VAR_FALLBACK_TEXT[key]?.[locale] ||
      VR_TEMPLATE_VAR_FALLBACK_TEXT[key]?.vi ||
      '',
  }));
}

/** @deprecated dùng getVoucherReminderTemplateVariablesFallback(locale) */
export const VR_REMINDER_TEMPLATE_VARIABLES_FALLBACK =
  getVoucherReminderTemplateVariablesFallback('vi');

/** Normalize translations array from API / form → always 4 langs */
export function normalizeTranslationsInput(
  raw: unknown
): ReminderTranslation[] {
  const map = new Map<string, ReminderTranslation>();
  if (Array.isArray(raw)) {
    raw.forEach((t: any) => {
      if (!t?.language) return;
      map.set(String(t.language), {
        language: t.language,
        title: String(t.title || '').trim(),
        body: String(t.body || '').trim(),
      });
    });
  }
  return VR_CONTENT_LOCALES.map(({ language }) => {
    const existing = map.get(language);
    return (
      existing || {
        language,
        title: '',
        body: '',
      }
    );
  });
}
